import { CheckCircleIcon } from '@components/Icons/CheckCircleIcon';
import { LoadingSpinnerIcon } from '@components/Icons/LoadingSpinnerIcon';
import { TriangleExclamationMarkIcon } from '@components/Icons/TriangleExclamationMarkIcon';
import { useDesktopAutoFocus } from '@lib/hooks/sizing/useDesktopAutoFocus';
import { getURLStatusMessage } from '@lib/utils/urlPreupload';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import {
  BaseModal,
  BaseModalRef,
} from '@pages/Search/components/MomentGrid/components/BaseModal/BaseModal';
import { useModalRegistration } from '@stores/ModalStore';
import { URLEntry, useUploadURL } from '@stores/UploadURL/UploadURLStore';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import css from './URLValidationModal.module.scss';

export type URLValidationModalRef = BaseModalRef;

type URLValidationModalProps = {
  onClose?: () => void;
  onOpen?: () => void;
  onSave?: (newUrls: string[]) => void;
};

const DEBOUNCE_DELAY = 800;

export const URLValidationModal = observer(
  forwardRef<URLValidationModalRef, URLValidationModalProps>(({ onClose, onOpen, onSave }, ref) => {
    const uploadURLStore = useUploadURL();
    const validationService = uploadURLStore.validationService;
    const baseModalRef = useRef<BaseModalRef>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState<string>('');
    const autoFocusOnDesktop = useDesktopAutoFocus();
    const [newUrlValue, setNewUrlValue] = useState<string>('');
    // Local state for URL entries - changes here don't affect the store until Save is clicked
    const [localUrlEntries, setLocalUrlEntries] = useState<URLEntry[]>([]);
    const [isOpen, setIsOpen] = useState(false);

    useModalRegistration('url-validation', isOpen);

    // Track original URLs when modal opens to identify new ones
    const originalUrlsRef = useRef<Set<string>>(new Set());
    // Track abort controllers and timeouts for cleanup
    const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
    const debounceTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

    // Expose the BaseModal's methods through the ref
    useImperativeHandle(ref, () => ({
      open: () => {
        baseModalRef.current?.open();
      },
      close: () => {
        handleCancelChanges();
      },
    }));

    const getStatusIcon = (status: string) => {
      switch (status) {
        case 'valid':
          return <CheckCircleIcon />;
        case 'error':
          return <TriangleExclamationMarkIcon />;
        case 'loading':
          return <LoadingSpinnerIcon />;
        default:
          return null;
      }
    };

    // Cleanup function for abort controllers and timeouts
    const cleanupEntry = (entryId: string) => {
      const controller = abortControllersRef.current.get(entryId);
      if (controller) {
        controller.abort();
        abortControllersRef.current.delete(entryId);
      }
      const timeout = debounceTimeoutsRef.current.get(entryId);
      if (timeout) {
        clearTimeout(timeout);
        debounceTimeoutsRef.current.delete(entryId);
      }
    };

    // Cleanup all entries
    const cleanupAll = () => {
      abortControllersRef.current.forEach((controller) => controller.abort());
      abortControllersRef.current.clear();
      debounceTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      debounceTimeoutsRef.current.clear();
    };

    // Validate a URL entry
    const validateEntry = async (entryId: string) => {
      const entry = localUrlEntries.find((e) => e.id === entryId);
      if (!entry) return;

      // Clear debounce timeout
      const existingTimeout = debounceTimeoutsRef.current.get(entryId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        debounceTimeoutsRef.current.delete(entryId);
      }

      // Cancel existing request
      const existingController = abortControllersRef.current.get(entryId);
      if (existingController) {
        existingController.abort();
      }

      // Create new abort controller
      const controller = new AbortController();
      abortControllersRef.current.set(entryId, controller);

      // Update status to loading and reset progress
      setLocalUrlEntries((prev) =>
        prev.map((e) =>
          e.id === entryId
            ? {
                ...e,
                status: 'loading' as const,
                errorMessage: '',
                loadingProgress: {
                  loaded: 0,
                  isStreaming: true,
                },
              }
            : e,
        ),
      );

      try {
        const validationResult = await validationService.url.validateURLContent(
          entry.url,
          controller.signal,
          // Phase 3: progress callback for "Loading... X videos found"
          (progress) => {
            setLocalUrlEntries((prev) =>
              prev.map((e) =>
                e.id === entryId
                  ? {
                      ...e,
                      loadingProgress: {
                        loaded: progress.loadedCount,
                        isStreaming: !progress.isComplete,
                      },
                    }
                  : e,
              ),
            );
          },
        );

        if (!controller.signal.aborted) {
          if (validationResult.valid && validationResult.unfurlData) {
            setLocalUrlEntries((prev) =>
              prev.map((e) =>
                e.id === entryId
                  ? {
                      ...e,
                      status: 'valid' as const,
                      unfurlData: validationResult.unfurlData!,
                      enhancedVideos: e.enhancedVideos || [],
                      isEnhancing: false,
                      loadingProgress: {
                        loaded: e.loadingProgress?.loaded ?? 0,
                        isStreaming: false,
                      },
                    }
                  : e,
              ),
            );
          } else {
            setLocalUrlEntries((prev) =>
              prev.map((e) =>
                e.id === entryId
                  ? {
                      ...e,
                      status: 'error' as const,
                      errorMessage:
                        validationResult.reason || 'Please enter a valid URL to upload from.',
                      loadingProgress: {
                        loaded: 0,
                        isStreaming: false,
                      },
                    }
                  : e,
              ),
            );
          }
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setLocalUrlEntries((prev) =>
            prev.map((e) =>
              e.id === entryId
                ? {
                    ...e,
                    status: 'error' as const,
                    errorMessage:
                      error instanceof Error
                        ? error.message
                        : 'Please enter a valid URL to upload from.',
                    loadingProgress: {
                      loaded: 0,
                      isStreaming: false,
                    },
                  }
                : e,
            ),
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          abortControllersRef.current.delete(entryId);
        }
      }
    };

    // Debounced validation
    const debounceValidateEntry = (entryId: string) => {
      // Clear existing timeout
      const existingTimeout = debounceTimeoutsRef.current.get(entryId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Set new timeout
      const timeout = setTimeout(() => {
        validateEntry(entryId);
        debounceTimeoutsRef.current.delete(entryId);
      }, DEBOUNCE_DELAY);

      debounceTimeoutsRef.current.set(entryId, timeout);
    };

    // Capture initial state when modal opens
    const captureInitialState = () => {
      // Cleanup any existing validations
      cleanupAll();

      // Store original URLs to identify new ones later
      originalUrlsRef.current = new Set(uploadURLStore.urlEntries.map((entry) => entry.url));

      // Create a deep copy of the entries to work with locally
      setLocalUrlEntries(
        uploadURLStore.urlEntries.map((entry) => ({
          ...entry,
          // Create new objects for nested properties to ensure deep copy
          unfurlData: entry.unfurlData ? { ...entry.unfurlData } : null,
          enhancedVideos: entry.enhancedVideos.map((video) => ({ ...video })),
        })),
      );
      setEditingId(null);
      setEditValue('');
      setNewUrlValue('');
    };

    // Validate entries when they change
    useEffect(() => {
      if (!isOpen) return;

      // Validate entries that need validation (new entries or entries with changed URLs)
      localUrlEntries.forEach((entry) => {
        // Only validate if status is loading and unfurlData is null (new/changed entries that haven't been validated yet)
        if (entry.status === 'loading' && entry.unfurlData === null) {
          // Check if there's already a validation in progress or scheduled
          const hasController = abortControllersRef.current.has(entry.id);
          const hasTimeout = debounceTimeoutsRef.current.has(entry.id);

          // Only start validation if not already in progress
          if (!hasController && !hasTimeout) {
            debounceValidateEntry(entry.id);
          }
        }
      });
    }, [localUrlEntries, isOpen]);

    const getStatusMessage = (entry: URLEntry) => {
      // Phase 3: show "Loading... X videos found" during SSE streaming
      if (entry.status === 'loading' && entry.loadingProgress && entry.loadingProgress.loaded > 0) {
        const n = entry.loadingProgress.loaded;
        return `Loading... ${n} media found`;
      }
      const statusMessage = getURLStatusMessage({
        urlStatus: entry.status,
        errorMessage: entry.errorMessage,
        loadingProgressCount:
          entry.status === 'loading' ? entry.loadingProgress?.loaded : undefined,
      });
      return statusMessage?.message || entry.errorMessage || 'Unknown status';
    };

    const getStatusClass = (status: string) => {
      switch (status) {
        case 'valid':
          return css.statusValid;
        case 'error':
          return css.statusError;
        case 'loading':
          return css.statusLoading;
        default:
          return '';
      }
    };

    const handleStartEdit = (entryId: string, currentUrl: string) => {
      setEditingId(entryId);
      setEditValue(currentUrl);
    };

    const handleInputClick = (entryId: string, currentUrl: string) => {
      // Only start editing if not already editing
      if (editingId !== entryId) {
        handleStartEdit(entryId, currentUrl);
      }
    };

    const handleCheckClick = (entryId: string) => {
      handleSaveEdit(entryId);
    };

    const handleCancelEdit = () => {
      setEditingId(null);
      setEditValue('');
    };

    const handleSaveEdit = (entryId: string) => {
      const trimmedValue = editValue.trim();
      if (!trimmedValue) {
        handleCancelEdit();
        return;
      }

      // Parse URLs from whitespace-separated input (same logic as setRawInput)
      const urls = trimmedValue
        .split(/\s+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      // Deduplicate URLs
      const uniqueUrls = [...new Set(urls)];

      if (uniqueUrls.length === 0) {
        handleCancelEdit();
        return;
      }

      // Cleanup the entry being edited
      cleanupEntry(entryId);

      if (uniqueUrls.length === 1) {
        // Single URL - update the existing entry in local state
        setLocalUrlEntries((prev) =>
          prev.map((entry) =>
            entry.id === entryId
              ? {
                  ...entry,
                  url: uniqueUrls[0],
                  status: 'loading' as const,
                  errorMessage: '',
                  unfurlData: null,
                  loadingProgress: {
                    loaded: 0,
                    isStreaming: false,
                  },
                }
              : entry,
          ),
        );
      } else {
        // Multiple URLs - replace the entry with multiple new entries
        const entryIndex = localUrlEntries.findIndex((e) => e.id === entryId);
        const newEntries: URLEntry[] = uniqueUrls.map((url, idx) => ({
          id: `${entryId}-split-${idx}-${Date.now()}`,
          url,
          status: 'loading' as const,
          errorMessage: '',
          unfurlData: null,
          enhancedVideos: [],
          isEnhancing: false,
          // This modal still manages its own AbortControllers in `abortControllersRef` and
          // calls validateURLContent directly, so it has nothing to unsubscribe from.
          //
          // Consequence, deliberately not fixed here: scrapes started from THIS modal are not
          // in the session cache, so cancelling it really does abort them and reopening
          // re-scrapes from zero. It can also run a second stream for a URL the cache is
          // already scraping, showing a different count for the same link. See
          // `.claude/scratchpad/TODO.md`.
          unsubscribeFromCache: null,
          debounceTimeout: null,
          loadingProgress: {
            loaded: 0,
            isStreaming: false,
          },
        }));

        setLocalUrlEntries((prev) => [
          ...prev.slice(0, entryIndex),
          ...newEntries,
          ...prev.slice(entryIndex + 1),
        ]);
      }

      handleCancelEdit();
    };

    const handleKeyDown = (e: React.KeyboardEvent, entryId: string) => {
      if (e.key === 'Enter') {
        handleSaveEdit(entryId);
      } else if (e.key === 'Escape') {
        handleCancelEdit();
      }
    };

    const handleDelete = (entryId: string) => {
      cleanupEntry(entryId);
      setLocalUrlEntries((prev) => prev.filter((entry) => entry.id !== entryId));
    };

    const handleAddNewUrl = () => {
      const trimmedValue = newUrlValue.trim();
      if (!trimmedValue) {
        return;
      }

      // Parse URLs from whitespace-separated input (same logic as setRawInput)
      const urls = trimmedValue
        .split(/\s+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      // Deduplicate URLs
      const uniqueUrls = [...new Set(urls)];

      if (uniqueUrls.length === 0) {
        setNewUrlValue('');
        return;
      }

      // Append new URLs to local entries
      const newEntries: URLEntry[] = uniqueUrls.map((url) => ({
        id: `new-${Date.now()}-${Math.random()}`,
        url,
        status: 'loading' as const,
        errorMessage: '',
        unfurlData: null,
        enhancedVideos: [],
        isEnhancing: false,
        // See the note on the split-entry construction above.
        unsubscribeFromCache: null,
        debounceTimeout: null,
        loadingProgress: {
          loaded: 0,
          isStreaming: false,
        },
      }));

      setLocalUrlEntries((prev) => [...prev, ...newEntries]);
      // Validation will be triggered by useEffect

      // Clear the input
      setNewUrlValue('');
    };

    const handleNewUrlKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleAddNewUrl();
      } else if (e.key === 'Escape') {
        setNewUrlValue('');
      }
    };

    const handleSaveChanges = () => {
      // Identify new URLs (URLs that weren't in the original set)
      const newUrls = localUrlEntries
        .filter((entry) => !originalUrlsRef.current.has(entry.url))
        .map((entry) => entry.url);

      // Apply local changes to the store, preserving validated data
      // This avoids re-fetching and re-processing thumbnails
      uploadURLStore.setValidatedEntries(localUrlEntries);

      // Notify parent about new URLs so they can be selected
      if (newUrls.length > 0 && onSave) {
        onSave(newUrls);
      }

      // Close the modal
      baseModalRef.current?.close();
    };

    const handleCancelChanges = () => {
      // Discard local changes and close the modal
      baseModalRef.current?.close();
    };

    const handleModalOpen = () => {
      setIsOpen(true);
      captureInitialState();
      onOpen?.();
    };

    const handleModalClose = () => {
      // Any close action should cancel changes (don't save)
      setIsOpen(false);
      // Cleanup all validations
      cleanupAll();
      // Reset local state when modal closes
      setLocalUrlEntries([]);
      setEditingId(null);
      setEditValue('');
      setNewUrlValue('');
      onClose?.();
    };

    const hasValidatingUrls = localUrlEntries.some((entry) => entry.status === 'loading');
    const hasErrorUrls = localUrlEntries.some((entry) => entry.status === 'error');
    const isEditing = editingId !== null;

    const footerContent = (
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', width: '100%' }}>
        <ActionButton variant="secondary" onClick={handleCancelChanges} padding={32}>
          Cancel changes
        </ActionButton>
        <Tooltip
          title={
            isEditing
              ? 'Please finish editing the URL before saving.'
              : hasValidatingUrls
                ? 'Please wait until all URLs are validated.'
                : hasErrorUrls
                  ? 'Media in URLs with errors will not be added.'
                  : ''
          }
          placement="top"
          arrow
          disableHoverListener={!isEditing && !hasValidatingUrls && !hasErrorUrls}
        >
          <span>
            <ActionButton
              variant="primary"
              onClick={handleSaveChanges}
              padding={32}
              disabled={hasValidatingUrls || isEditing}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {hasErrorUrls && (
                  <span className={css.warningIcon}>
                    <TriangleExclamationMarkIcon />
                  </span>
                )}
                Save changes
              </span>
            </ActionButton>
          </span>
        </Tooltip>
      </div>
    );

    return (
      <BaseModal
        ref={baseModalRef}
        title="Editing URLs"
        onClose={handleModalClose}
        onOpen={handleModalOpen}
        footerContent={footerContent}
        className={css.urlValidationModal}
      >
        <div className={css.content}>
          <div className={css.urlList}>
            {localUrlEntries.length === 0 ? (
              <div className={css.emptyState}>No URLs to display.</div>
            ) : (
              localUrlEntries.map((entry) => (
                <div key={entry.id} className={css.urlItem}>
                  <TextField
                    value={editingId === entry.id ? editValue : entry.url}
                    onChange={(e) => {
                      if (editingId === entry.id) {
                        setEditValue(e.target.value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (editingId === entry.id) {
                        handleKeyDown(e, entry.id);
                      }
                    }}
                    onClick={() => handleInputClick(entry.id, entry.url)}
                    onBlur={(e) => {
                      // Don't save on blur if clicking the check button
                      const relatedTarget = e.relatedTarget as HTMLElement;
                      if (relatedTarget?.closest('button')) {
                        return;
                      }
                      if (editingId === entry.id) {
                        // Save and validate on blur
                        handleSaveEdit(entry.id);
                      }
                    }}
                    autoFocus={autoFocusOnDesktop && editingId === entry.id}
                    fullWidth
                    size="small"
                    className={classNames(
                      css.urlInput,
                      getStatusClass(entry.status),
                      editingId === entry.id && css.editing,
                    )}
                    slotProps={{
                      input: {
                        readOnly: editingId !== entry.id,
                        endAdornment: (
                          <InputAdornment position="end">
                            <div className={css.adornmentContainer}>
                              {editingId === entry.id ? (
                                // Show check icon when editing
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCheckClick(entry.id);
                                  }}
                                  className={css.checkButton}
                                  title="Save URL"
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              ) : (
                                // Show status icon, edit button, delete button when not editing
                                <>
                                  <Tooltip title={getStatusMessage(entry)} placement="top" arrow>
                                    <span
                                      className={classNames(
                                        css.statusIconWrapper,
                                        getStatusClass(entry.status),
                                      )}
                                    >
                                      {getStatusIcon(entry.status)}
                                    </span>
                                  </Tooltip>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStartEdit(entry.id, entry.url);
                                    }}
                                    className={css.editButton}
                                    title="Edit URL"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(entry.id);
                                    }}
                                    className={css.deleteButton}
                                    title="Delete URL"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </>
                              )}
                            </div>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </div>
              ))
            )}
            {/* Add new URL input */}
            <div className={css.urlItem}>
              <TextField
                value={newUrlValue}
                onChange={(e) => setNewUrlValue(e.target.value)}
                onKeyDown={handleNewUrlKeyDown}
                onBlur={(e) => {
                  // Don't add on blur if clicking the check button
                  const relatedTarget = e.relatedTarget as HTMLElement;
                  if (relatedTarget?.closest('button')) {
                    return;
                  }
                  handleAddNewUrl();
                }}
                placeholder="Add another URL"
                fullWidth
                size="small"
                className={css.urlInput}
                slotProps={{
                  input: {
                    endAdornment: newUrlValue.trim() ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddNewUrl();
                          }}
                          className={css.checkButton}
                          title="Add URL"
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                }}
              />
            </div>
          </div>
        </div>
      </BaseModal>
    );
  }),
);
